import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import {
  CURRENT_SHIFT_ID,
  emptyShift,
  seedShift,
  shiftFromDoc,
  shiftToMap,
  type Shift,
} from '@/models/shift';
import { formatAmount, parseAmountValue } from '@/lib/amount';
import { toIsoSec } from '@/lib/date';
import { finalizePatients } from '@/services/firestore/patients';
import { finalizeActs } from '@/services/firestore/acts';

const shiftDoc = (uid: string, shiftId: string) =>
  doc(db, 'users', uid, 'shifts', shiftId);
const currentShiftDoc = (uid: string) => shiftDoc(uid, CURRENT_SHIFT_ID);

export async function getCurrentShift(uid: string): Promise<Shift> {
  const snap = await getDoc(currentShiftDoc(uid));
  if (!snap.exists()) return emptyShift();
  return shiftFromDoc(CURRENT_SHIFT_ID, snap.data());
}

export async function startNewShift(args: {
  uid: string;
  mainPlace: string;
  mdType: string;
  facturationType: string;
}): Promise<Shift | null> {
  const existing = await getDoc(currentShiftDoc(args.uid));
  if (existing.exists()) return null;

  const now = new Date();
  const start = new Date(now);
  start.setMinutes(0, 0, 0);
  const end = new Date(start);
  end.setHours(end.getHours() + 8);

  const seeded = seedShift({
    userId: args.uid,
    place: args.mainPlace,
    mdType: args.mdType || 'cfpc',
    facturationType: args.facturationType || 'mixte',
    start,
    end,
  });

  await setDoc(currentShiftDoc(args.uid), shiftToMap(seeded), { merge: true });
  return { ...seeded, id: CURRENT_SHIFT_ID };
}

// Mirrors Flutter _applyShiftTotalDelta — in-memory cache is the source of truth.
// On every act add/delete/modify, the caller passes the new cached totals;
// we write them to Firestore without a prior read.
export async function writeShiftTotals(args: {
  uid: string;
  newTotal: number;
  newRawTotal: number;
}): Promise<void> {
  await updateDoc(currentShiftDoc(args.uid), {
    totalBilled: formatAmount(args.newTotal),
    totalRawBilled: formatAmount(args.newRawTotal),
  });
}

// Fallback path when no in-memory cache exists (tests, fresh load).
export async function applyShiftTotalDeltaReadModifyWrite(args: {
  uid: string;
  deltaBilled: number;
  deltaRawBilled: number;
}): Promise<{ newTotal: number; newRawTotal: number } | null> {
  const snap = await getDoc(currentShiftDoc(args.uid));
  if (!snap.exists()) return null;
  const shift = shiftFromDoc(CURRENT_SHIFT_ID, snap.data());
  const newTotal = parseAmountValue(shift.totalBilled) + args.deltaBilled;
  const newRawTotal = parseAmountValue(shift.totalRawBilled) + args.deltaRawBilled;
  await updateDoc(snap.ref, {
    totalBilled: formatAmount(newTotal),
    totalRawBilled: formatAmount(newRawTotal),
  });
  return { newTotal, newRawTotal };
}

export async function updateCurrentShiftFields(
  uid: string,
  fields: Partial<Record<keyof Shift, string | null>>,
): Promise<void> {
  await updateDoc(currentShiftDoc(uid), fields as Record<string, unknown>);
}

export async function finalizeCurrentShiftForSubmission(args: {
  uid: string;
  start: Date;
  end: Date;
}): Promise<string> {
  const snap = await getDoc(currentShiftDoc(args.uid));
  if (!snap.exists()) throw new Error('no current shift');
  const shift = shiftFromDoc(CURRENT_SHIFT_ID, snap.data());
  if (!shift.finalId) throw new Error('current shift missing finalId');

  const finalized: Shift = {
    ...shift,
    id: shift.finalId,
    dateStarted: toIsoSec(args.start),
    dateEnded: toIsoSec(args.end),
  };

  // Mirror Flutter: copy the shift doc + reassign every patient and act
  // currently bound to `current_shift` so the backend sees a complete record
  // under the final shift ID. Without this the report Lambda finds no
  // patients/acts and silently writes a reportUrl pointing to a missing PDF.
  await Promise.all([
    setDoc(shiftDoc(args.uid, shift.finalId), shiftToMap(finalized), {
      merge: true,
    }),
    finalizePatients({ uid: args.uid, finalShiftId: shift.finalId }),
    finalizeActs({ uid: args.uid, finalShiftId: shift.finalId }),
  ]);

  return shift.finalId;
}

export async function clearCurrentShift(uid: string): Promise<void> {
  await deleteDoc(currentShiftDoc(uid));
}

// Discard the in-progress shift entirely: deletes every patient + act
// currently tied to `current_shift`, then deletes the shift doc. Use when the
// user cancels a shift, not when submitting. Different from clearCurrentShift
// which only removes the shift doc (used after a successful submission, where
// patients/acts have already been moved to the final shift id).
export async function discardCurrentShift(uid: string): Promise<void> {
  const patientsQ = query(
    collection(db, 'users', uid, 'patients'),
    where('shiftId', '==', CURRENT_SHIFT_ID),
  );
  const actsQ = query(
    collection(db, 'users', uid, 'acts'),
    where('shiftId', '==', CURRENT_SHIFT_ID),
  );
  const [patients, acts] = await Promise.all([
    getDocs(patientsQ),
    getDocs(actsQ),
  ]);

  const BATCH = 450;
  async function bulkDelete(snap: typeof patients) {
    for (let i = 0; i < snap.docs.length; i += BATCH) {
      const batch = writeBatch(db);
      for (const d of snap.docs.slice(i, i + BATCH)) batch.delete(d.ref);
      await batch.commit();
    }
  }
  await Promise.all([bulkDelete(patients), bulkDelete(acts)]);
  await deleteDoc(currentShiftDoc(uid));
}

// Watches the finalized shift doc for a `reportUrl` field — set by the backend
// after wkhtmltopdf rendering completes. Resolves with the URL or rejects on timeout.
export function listenForReportUrl(args: {
  uid: string;
  shiftId: string;
  timeoutMs: number;
}): { promise: Promise<string>; cancel: () => void } {
  let unsub: Unsubscribe = () => {};
  let timer: ReturnType<typeof setTimeout>;
  let settled = false;

  const stop = () => {
    settled = true;
    clearTimeout(timer);
    unsub();
  };

  const promise = new Promise<string>((resolve, reject) => {
    unsub = onSnapshot(shiftDoc(args.uid, args.shiftId), (snap) => {
      if (settled) return;
      const url = snap.data()?.reportUrl;
      if (typeof url === 'string' && url.length > 0) {
        stop();
        resolve(url);
      }
    });
    timer = setTimeout(() => {
      if (settled) return;
      stop();
      reject(new Error('report timeout'));
    }, args.timeoutMs);
  });

  return { promise, cancel: stop };
}
